import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Stack,
  Divider
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';

/**
 * ProfileTab - Browse and add profile items to resume
 * 
 * Shows hierarchical profile structure:
 * - Experience (by company)
 * - Highlights
 * - Projects
 * - Custom sections
 */
const ProfileTab = ({ profile, selectedItems, graphData, onToggleSelection, onAddItem, onRemoveItem }) => {
  const [expandedCompanies, setExpandedCompanies] = useState({});

  // Use graphData if available, fallback to profile prop for backwards compatibility
  const profileData = profile || (graphData ? { 
    experience: [], // GraphData has different structure - will need conversion
    highlights: [],
    projects: []
  } : null);
  
  if (!profileData && !graphData) {
    return (
      <Box sx={{ textAlign: 'center', py: 3 }}>
        <Typography variant="body2" color="text.secondary">
          Loading profile...
        </Typography>
      </Box>
    );
  }

  // Check if an item is selected
  const isSelected = (itemId) => {
    return selectedItems.some(item => item.id === itemId);
  };

  // Handle toggle company expansion
  const handleToggleCompany = (companyIndex) => {
    setExpandedCompanies(prev => ({
      ...prev,
      [companyIndex]: !prev[companyIndex]
    }));
  };

  // Count selected items for a company
  const countSelectedInCompany = (experience) => {
    if (!experience.accomplishments) return 0;
    return experience.accomplishments.filter(acc =>
      isSelected(`experience-${experience.company}-${acc.text}`)
    ).length;
  };

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        Add items to your resume
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" mb={2}>
        {selectedItems.length} items selected
      </Typography>

      {/* EXPERIENCE SECTION */}
      {profileData.experience && profileData.experience.length > 0 && (
        <Box mb={2}>
          <Typography variant="caption" fontWeight="bold" color="text.secondary" gutterBottom display="block">
            📁 EXPERIENCE
          </Typography>
          
          {profileData.experience.map((exp, expIndex) => {
            const selectedCount = countSelectedInCompany(exp);
            const totalCount = exp.accomplishments?.length || 0;
            
            return (
              <Accordion
                key={expIndex}
                expanded={expandedCompanies[expIndex] || false}
                onChange={() => handleToggleCompany(expIndex)}
                elevation={0}
                sx={{ border: '1px solid', borderColor: 'divider', mb: 1 }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box flex={1}>
                    <Typography variant="body2" fontWeight="medium">
                      {exp.company || 'Company'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {exp.title || 'Position'}
                    </Typography>
                    {selectedCount > 0 && (
                      <Chip
                        label={`${selectedCount}/${totalCount}`}
                        size="small"
                        color="primary"
                        sx={{ ml: 1, height: 18, fontSize: '0.7rem' }}
                      />
                    )}
                  </Box>
                </AccordionSummary>
                
                <AccordionDetails>
                  {exp.accomplishments && exp.accomplishments.length > 0 ? (
                    exp.accomplishments.map((acc, accIndex) => {
                      // Use existing ID if available, otherwise generate one
                      const accText = typeof acc === 'string' ? acc : (acc.text || acc.description || '');
                      const itemId = acc.id || `experience-${exp.company}-${accText}`;
                      const selected = isSelected(itemId);
                      
                      return (
                        <Box
                          key={accIndex}
                          sx={{
                            p: 1,
                            mb: 1,
                            bgcolor: selected ? 'primary.50' : 'transparent',
                            border: '1px solid',
                            borderColor: selected ? 'primary.main' : 'divider',
                            borderRadius: 1
                          }}
                        >
                          <Stack direction="row" spacing={1} alignItems="flex-start">
                            <Box flex={1}>
                              <Typography variant="caption" display="block">
                                {acc.text || acc}
                              </Typography>
                              {acc.impact && (
                                <Typography variant="caption" color="text.secondary" display="block">
                                  Impact: {acc.impact}
                                </Typography>
                              )}
                            </Box>
                            <IconButton
                              size="small"
                              onClick={() => {
                                // Use unified selection system
                                if (onToggleSelection) {
                                  onToggleSelection(itemId);
                                } else {
                                  // Fallback to old system for backwards compatibility
                                  if (selected) {
                                    onRemoveItem(itemId);
                                  } else {
                                    onAddItem({
                                      id: itemId,
                                      type: 'experience',
                                      company: exp.company,
                                      title: exp.title,
                                      text: acc.text || acc,
                                      impact: acc.impact,
                                      duration: `${exp.start_date || ''} - ${exp.end_date || 'Present'}`
                                    });
                                  }
                                }
                              }}
                              color={selected ? 'primary' : 'default'}
                            >
                              {selected ? <RemoveIcon fontSize="small" /> : <AddIcon fontSize="small" />}
                            </IconButton>
                          </Stack>
                        </Box>
                      );
                    })
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      No accomplishments listed
                    </Typography>
                  )}
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>
      )}

      {/* HIGHLIGHTS SECTION */}
      {profileData.highlights && profileData.highlights.length > 0 && (
        <Box mb={2}>
          <Divider sx={{ my: 2 }} />
          <Typography variant="caption" fontWeight="bold" color="text.secondary" gutterBottom display="block">
            📁 HIGHLIGHTS
          </Typography>
          
          {profileData.highlights.map((highlight, hIndex) => (
            <Box key={hIndex} mb={1}>
              <Typography variant="caption" fontWeight="medium" display="block" mb={0.5}>
                {highlight.category || 'Highlight'}
              </Typography>
              {highlight.items && highlight.items.map((item, iIndex) => {
                const itemText = typeof item === 'string' ? item : (item.text || item);
                const itemId = item.id || `highlight-${highlight.category}-${itemText}`;
                const selected = isSelected(itemId);
                
                return (
                  <Box
                    key={iIndex}
                    sx={{
                      p: 1,
                      mb: 0.5,
                      bgcolor: selected ? 'primary.50' : 'transparent',
                      border: '1px solid',
                      borderColor: selected ? 'primary.main' : 'divider',
                      borderRadius: 1
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="flex-start">
                      <Box flex={1}>
                        <Typography variant="caption">
                          {item.text || item}
                        </Typography>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={() => {
                          // Use unified selection system
                          if (onToggleSelection) {
                            onToggleSelection(itemId);
                          } else {
                            // Fallback to old system
                            if (selected) {
                              onRemoveItem(itemId);
                            } else {
                              onAddItem({
                                id: itemId,
                                type: 'highlight',
                                category: highlight.category,
                                text: item.text || item
                              });
                            }
                          }
                        }}
                        color={selected ? 'primary' : 'default'}
                      >
                        {selected ? <RemoveIcon fontSize="small" /> : <AddIcon fontSize="small" />}
                      </IconButton>
                    </Stack>
                  </Box>
                );
              })}
            </Box>
          ))}
        </Box>
      )}

      {/* PROJECTS SECTION */}
      {profileData.projects && profileData.projects.length > 0 && (
        <Box mb={2}>
          <Divider sx={{ my: 2 }} />
          <Typography variant="caption" fontWeight="bold" color="text.secondary" gutterBottom display="block">
            📁 PROJECTS
          </Typography>
          
          {profileData.projects.map((project, pIndex) => {
            const itemId = project.id || `project-${project.name}`;
            const selected = isSelected(itemId);
            
            return (
              <Box
                key={pIndex}
                sx={{
                  p: 1,
                  mb: 1,
                  bgcolor: selected ? 'primary.50' : 'transparent',
                  border: '1px solid',
                  borderColor: selected ? 'primary.main' : 'divider',
                  borderRadius: 1
                }}
              >
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <Box flex={1}>
                    <Typography variant="caption" fontWeight="medium" display="block">
                      {project.name || 'Project'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {project.description}
                    </Typography>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() => {
                      // Use unified selection system
                      if (onToggleSelection) {
                        onToggleSelection(itemId);
                      } else {
                        // Fallback to old system
                        if (selected) {
                          onRemoveItem(itemId);
                        } else {
                          onAddItem({
                            id: itemId,
                            type: 'project',
                            name: project.name,
                            description: project.description,
                            technologies: project.technologies
                          });
                        }
                      }
                    }}
                    color={selected ? 'primary' : 'default'}
                  >
                    {selected ? <RemoveIcon fontSize="small" /> : <AddIcon fontSize="small" />}
                  </IconButton>
                </Stack>
              </Box>
            );
          })}
        </Box>
      )}

      {/* Education and Skills are always included */}
      <Divider sx={{ my: 2 }} />
      <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center" mb={1}>
          <CheckCircleIcon fontSize="small" color="success" />
          <Typography variant="caption" fontWeight="medium">
            Always Included
          </Typography>
        </Stack>
        <Typography variant="caption" color="text.secondary" display="block">
          • Education
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block">
          • Skills
        </Typography>
      </Box>
    </Box>
  );
};

export default ProfileTab;
